// 生成任务：排队（同时 2 个）→ 上传 → 等模型 → 五官锁定 → 存本地。带进度估算。

import { type StyleRequest, styleKey } from "../../shared/styles";
import { AuthError, describeError, generateImage } from "./api";
import { type Photo, put, type Result, unpackAnalysis } from "./db";
import { blobToCanvas, canvasToBlob } from "./image";
import { lockFace } from "./lock";
import { renderThumb } from "./render";
import { loadVision } from "./vision";

export type Stage = "queued" | "models" | "upload" | "ai" | "lock" | "done" | "error";

export interface Job {
  id: string;
  photoId: string;
  memberId: string;
  req: StyleRequest;
  stage: Stage;
  /** 0-100 */
  progress: number;
  startedAt: number;
  aiStartedAt?: number;
  error?: string;
  modelLoad?: { loaded: number; total: number };
}

type Listener = () => void;

const MAX_PARALLEL = 2;
const jobs = new Map<string, Job>();
const listeners = new Set<Listener>();
const waiting: { job: Job; photo: Photo }[] = [];
let running = 0;
let ticker: number | undefined;

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  for (const fn of listeners) fn();
}

export function getJob(id: string): Job | undefined {
  return jobs.get(id);
}

export function allJobs(): Job[] {
  return [...jobs.values()];
}

export function resultId(photoId: string, req: StyleRequest): string {
  return `${photoId}|${styleKey(req)}`;
}

export function enqueue(photo: Photo, req: StyleRequest): Job {
  const id = resultId(photo.id, req);
  const existing = jobs.get(id);
  if (existing && existing.stage !== "error" && existing.stage !== "done") return existing;
  const job: Job = {
    id,
    photoId: photo.id,
    memberId: photo.memberId,
    req,
    stage: "queued",
    progress: 0,
    startedAt: Date.now(),
  };
  jobs.set(id, job);
  waiting.push({ job, photo });
  emit();
  pump();
  return job;
}

export function cancelQueued(photoId?: string) {
  for (let i = waiting.length - 1; i >= 0; i--) {
    if (!photoId || waiting[i].job.photoId === photoId) {
      jobs.delete(waiting[i].job.id);
      waiting.splice(i, 1);
    }
  }
  emit();
}

function pump() {
  while (running < MAX_PARALLEL && waiting.length) {
    const next = waiting.shift()!;
    running++;
    run(next.job, next.photo).finally(() => {
      running--;
      pump();
    });
  }
  if (!ticker && (running || waiting.length)) {
    ticker = window.setInterval(tick, 200);
  }
}

/** 等待模型期间按历史耗时平滑推进进度条 */
function tick() {
  let active = false;
  for (const j of jobs.values()) {
    if (j.stage === "ai" && j.aiStartedAt) {
      const t = Date.now() - j.aiStartedAt;
      const expected = expectedMs();
      j.progress = Math.min(92, 12 + 80 * (1 - Math.exp((-2.2 * t) / expected)));
      active = true;
    } else if (j.stage !== "done" && j.stage !== "error") {
      active = true;
    }
  }
  emit();
  if (!active && ticker) {
    clearInterval(ticker);
    ticker = undefined;
  }
}

const AVG_KEY = "jq.avgMs";
export function expectedMs(): number {
  const v = Number(localStorage.getItem(AVG_KEY));
  return v > 3000 ? v : 30_000;
}
function recordDuration(ms: number) {
  const prev = Number(localStorage.getItem(AVG_KEY)) || ms;
  localStorage.setItem(AVG_KEY, String(Math.round(prev * 0.6 + ms * 0.4)));
}

let accessCode = localStorage.getItem("jq.code") ?? "";
export function setAccessCode(code: string) {
  accessCode = code;
  localStorage.setItem("jq.code", code);
}
export function getAccessCode() {
  return accessCode;
}

async function run(job: Job, photo: Photo) {
  const update = (patch: Partial<Job>) => {
    Object.assign(job, patch);
    emit();
  };
  try {
    update({ stage: "models", progress: 2, startedAt: Date.now() });
    await loadVision((loaded, total) => update({ modelLoad: { loaded, total }, progress: 2 + (loaded / total) * 6 }));

    update({ stage: "upload", progress: 8 });
    const t0 = Date.now();
    update({ stage: "ai", progress: 12, aiStartedAt: t0 });
    const { blob, cache } = await generateImage(photo, job.req, accessCode);
    if (cache !== "HIT") recordDuration(Date.now() - t0);

    update({ stage: "lock", progress: 93 });
    const base = await blobToCanvas(photo.base);
    const gen = await blobToCanvas(blob, Math.max(photo.width, photo.height) * 1.2);
    const analysis = unpackAnalysis(photo.analysis);
    const lock = await lockFace(base, analysis, gen);
    update({ progress: 97 });

    const aligned = await canvasToBlob(lock.aligned, "image/jpeg", 0.9);
    const result: Result = {
      id: job.id,
      photoId: photo.id,
      memberId: photo.memberId,
      styleKey: styleKey(job.req),
      styleId: job.req.styleId,
      colorId: job.req.colorId,
      custom: job.req.custom,
      aligned,
      mask: lock.mask,
      mw: lock.mw,
      mh: lock.mh,
      locked: lock.locked,
      favorite: false,
      createdAt: Date.now(),
      duration: Date.now() - job.startedAt,
      thumb: await renderThumb(base, analysis, lock.aligned, lock.mask, lock.mw, lock.mh),
    };
    await put("results", result);
    update({ stage: "done", progress: 100 });
    // 完成的任务留一会儿用于动画，然后清掉
    setTimeout(() => {
      if (jobs.get(job.id) === job) {
        jobs.delete(job.id);
        emit();
      }
    }, 1200);
  } catch (err) {
    console.error("[generate]", err);
    update({ stage: "error", error: describeError(err) });
    if (err instanceof AuthError) window.dispatchEvent(new CustomEvent("jq:auth"));
  }
}

export function dismiss(id: string) {
  const j = jobs.get(id);
  if (j && (j.stage === "error" || j.stage === "done")) {
    jobs.delete(id);
    emit();
  }
}
